import { useState, useMemo, lazy, Suspense } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { extractErrorMessage, getMonthName, ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE } from "@/shared/utils";
import { PERMISSIONS } from "@/shared/consts";
import { useLocale, useAuth, useBreadcrumbOverrides } from "@/shared/hooks";
import { Prompt, PromptForm } from "@/shared/ui";
import { isBudgetLimitErrorV2, isOverrideableError } from "@/features/accounting/smeta/types";
import { useOverrideState } from "@/features/accounting/smeta/hooks/useOverrideState";
import { extractSmetaErrorMessage } from "@/features/accounting/smeta/utils/errorDispatch";
import { useGetContractorPayrollDetailQuery, useApproveGPHMutation, useMarkGPHPaidMutation } from "../../api";
import type { GPHPayment } from "../../types";
import { computeAggregatedStatus, computeDetailStats } from "../../utils/computeAggregatedStatus";
import {
  ContractorPayrollDetailHeader,
  ContractorPayrollDetailStatCards,
  ContractorPayrollDetailSkeleton,
} from "./components";

const GPHPaymentsTable = lazy(() =>
  import("../../components/GPHPaymentsTable").then((m) => ({ default: m.default })),
);
const GPHPaymentDetailModal = lazy(() =>
  import("../../components/GPHPaymentDetailModal").then((m) => ({ default: m.default })),
);
const GPHApproveModal = lazy(() =>
  import("../../components/GPHApproveModal").then((m) => ({ default: m.default })),
);
const OverrideReasonModal = lazy(() =>
  import("@/features/accounting/smeta/components/OverrideReasonModal").then((m) => ({ default: m.default })),
);

export default function ContractorPayrollDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation("ContractorPayrollPage");
  const locale = useLocale();
  const { hasPermission } = useAuth();

  const yearParam = searchParams.get("year");
  const monthParam = searchParams.get("month");
  const year = Number(yearParam);
  const month = Number(monthParam);
  const isValidPeriod = !!yearParam && !!monthParam && !isNaN(year) && !isNaN(month) && month >= 1 && month <= 12;

  const monthName = isValidPeriod ? getMonthName(month, locale) : "";

  useBreadcrumbOverrides({ contractorPayrollLabel: isValidPeriod ? `${monthName} ${year}` : "" });

  const { data, isLoading, isError } = useGetContractorPayrollDetailQuery({ year, month }, { skip: !isValidPeriod });
  const [approveGPH] = useApproveGPHMutation();
  const [markGPHPaid] = useMarkGPHPaidMutation();

  const [gphDetailPayment, setGphDetailPayment] = useState<GPHPayment | null>(null);
  const [confirmApproveGPH, setConfirmApproveGPH] = useState<GPHPayment | null>(null);
  const [confirmMarkGPHPaid, setConfirmMarkGPHPaid] = useState<GPHPayment | null>(null);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [markingPaidId, setMarkingPaidId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const { t: tSmeta } = useTranslation("SmetaPage");
  const gphOverride = useOverrideState<{ paymentId: number; applyMrpDeduction: boolean }>();

  const canApprove = hasPermission(PERMISSIONS.ACCOUNTING.APPROVE_GPH);
  const canMarkPaid = hasPermission(PERMISSIONS.ACCOUNTING.PAY_GPH);

  const aggregatedStatus = useMemo(
    () => computeAggregatedStatus(data?.payments ?? []),
    [data?.payments],
  );

  const stats = useMemo(
    () => computeDetailStats(data?.payments ?? []),
    [data?.payments],
  );

  if (!isValidPeriod) {
    navigate(ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE, { replace: true });
    return null;
  }

  async function handleApproveGPHConfirm(applyMrpDeduction: boolean) {
    if (!confirmApproveGPH || approvingId !== null) return;
    const paymentId = confirmApproveGPH.id;
    try {
      setApprovingId(paymentId);
      await approveGPH({ id: paymentId, body: { apply_mrp_deduction: applyMrpDeduction } }).unwrap();
      setPrompt({
        title: t("payroll.messages.gphApproveSuccessTitle"),
        text: t("payroll.messages.gphApproveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      if (isBudgetLimitErrorV2(err)) {
        if (err.data.failure_type === "invalid_input") {
          setPrompt({
            title: t("payroll.messages.errorTitle"),
            text: tSmeta("errors.invalid_input"),
            variant: "error",
          });
          return;
        }
        if (isOverrideableError(err.data)) {
          gphOverride.request(err.data, { paymentId, applyMrpDeduction });
          return;
        }
        setPrompt({
          title: tSmeta("messages.errorTitle"),
          text: extractSmetaErrorMessage(err.data, tSmeta),
          variant: "error",
        });
        return;
      }
      setPrompt({
        title: t("payroll.messages.errorTitle"),
        text: extractErrorMessage(err, "payroll.messages.gphApproveErrorText", t),
        variant: "error",
      });
    } finally {
      setApprovingId(null);
      setConfirmApproveGPH(null);
    }
  }

  async function handleGPHOverrideConfirm(overrideFields: { override_reason: string }) {
    if (!gphOverride.pending || approvingId !== null) return;
    const { paymentId, applyMrpDeduction } = gphOverride.pending.retryArgs;
    gphOverride.clear();
    try {
      setApprovingId(paymentId);
      await approveGPH({
        id: paymentId,
        body: { apply_mrp_deduction: applyMrpDeduction, override_reason: overrideFields.override_reason },
      }).unwrap();
      setPrompt({
        title: t("payroll.messages.gphApproveSuccessTitle"),
        text: t("payroll.messages.gphApproveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      if (isBudgetLimitErrorV2(err)) {
        if (err.data.failure_type === "invalid_input") {
          setPrompt({ title: t("payroll.messages.errorTitle"), text: tSmeta("errors.invalid_input"), variant: "error" });
          return;
        }
        if (isOverrideableError(err.data)) {
          gphOverride.request(err.data, { paymentId, applyMrpDeduction });
          return;
        }
        setPrompt({ title: tSmeta("messages.errorTitle"), text: extractSmetaErrorMessage(err.data, tSmeta), variant: "error" });
      } else {
        setPrompt({ title: t("payroll.messages.errorTitle"), text: extractErrorMessage(err, "payroll.messages.gphApproveErrorText", t), variant: "error" });
      }
    } finally {
      setApprovingId(null);
    }
  }

  async function handleMarkGPHPaidConfirm() {
    if (!confirmMarkGPHPaid || markingPaidId !== null) return;
    try {
      setMarkingPaidId(confirmMarkGPHPaid.id);
      await markGPHPaid({ id: confirmMarkGPHPaid.id }).unwrap();
      setPrompt({
        title: t("payroll.messages.gphMarkPaidSuccessTitle"),
        text: t("payroll.messages.gphMarkPaidSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("payroll.messages.errorTitle"),
        text: extractErrorMessage(err, "payroll.messages.gphMarkPaidErrorText", t),
        variant: "error",
      });
    } finally {
      setMarkingPaidId(null);
      setConfirmMarkGPHPaid(null);
    }
  }

  if (isLoading) {
    return (
      <section className="h-full overflow-hidden">
        <div className="h-full component-scroll">
          <ContractorPayrollDetailSkeleton />
        </div>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section className="h-full overflow-hidden">
        <div className="h-full component-scroll">
          <ContractorPayrollDetailHeader month={month} year={year} locale={locale} aggregatedStatus="all_pending" />
          <p className="text-body-regular-md content-action-neutral">{t("messages.error")}</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="h-full overflow-hidden">
        <div className="h-full component-scroll">
          <ContractorPayrollDetailHeader
            month={month}
            year={year}
            locale={locale}
            aggregatedStatus={aggregatedStatus}
          />

          <ContractorPayrollDetailStatCards
            contractorCount={stats.contractorCount}
            totalGross={stats.totalGross}
            totalWithheld={stats.totalWithheld}
            totalNet={stats.totalNet}
            locale={locale}
          />

          <div className="px-6 py-5 surface-base-fill radius-3xl">
            <Suspense fallback={<ContractorPayrollDetailSkeleton />}>
              <GPHPaymentsTable
                payments={data.payments}
                onViewDetails={setGphDetailPayment}
                onApprove={canApprove ? setConfirmApproveGPH : undefined}
                onMarkPaid={canMarkPaid ? setConfirmMarkGPHPaid : undefined}
                approvingGPHId={approvingId}
                markingGPHPaidId={markingPaidId}
              />
            </Suspense>
          </div>
        </div>
      </section>

      {gphDetailPayment && (
        <Suspense fallback={null}>
          <GPHPaymentDetailModal
            payment={gphDetailPayment}
            constants_used={data.constants_used}
            onClose={() => setGphDetailPayment(null)}
          />
        </Suspense>
      )}

      {confirmApproveGPH && (
        <Suspense fallback={null}>
          <GPHApproveModal
            payment={confirmApproveGPH}
            constants_used={data.constants_used}
            onClose={() => setConfirmApproveGPH(null)}
            onConfirm={handleApproveGPHConfirm}
            isLoading={approvingId !== null}
          />
        </Suspense>
      )}

      {gphOverride.pending && (
        <Suspense fallback={null}>
          <OverrideReasonModal
            errorData={gphOverride.pending.errorData}
            onClose={gphOverride.clear}
            onConfirm={handleGPHOverrideConfirm}
            isLoading={approvingId !== null}
          />
        </Suspense>
      )}

      {confirmMarkGPHPaid !== null && (
        <PromptForm
          title={t("payroll.confirm.gphMarkPaidTitle")}
          text={t("payroll.confirm.gphMarkPaidText")}
          variant="success"
          onClose={() => setConfirmMarkGPHPaid(null)}
          onConfirm={handleMarkGPHPaidConfirm}
          isLoading={markingPaidId !== null}
          confirmText={t("payroll.confirm.gphMarkPaidConfirm")}
          cancelText={t("payroll.confirm.cancel")}
          namespace="ContractorPayrollPage"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="ContractorPayrollPage"
        />
      )}
    </>
  );
}
